<template>
  <div
    class="IsoMoveAnimation"
  >
    <IsoChatOverlay
      ref="overlayRef"
      :vector="overlayVector"
    >
      <IsoChat
        v-if="isAnimatingBlock"
        ref="userChatRef"
        type="User"
        :fragments="['How much should', 'I withhold for taxes?']"
      />

      <IsoChatGroup v-if="isAnimatingGraph">
        <IsoChat
          ref="systemChatRef"
          type="System"
          :fragments="['I found a', 'few opportunities', 'to reduce', 'your taxes:']"
        />
      </IsoChatGroup>

      <IsoChatGroup v-if="isAnimatingGraph">
        <IsoChat
          ref="taxLossHarvestingChatRef"
          type="System"
          :fragments="['Tax-loss', 'harvesting']"
        />
        <IsoChat
          ref="payrollOptimizationChatRef"
          type="System"
          :fragments="['Open an HSA', 'or FSA']"
        />
        <IsoChat
          ref="selfChatRef"
          type="System"
          :fragments="['Mega backdoor', 'Roth conversion']"
        />
        <IsoChat
          ref="confirmChatRef"
          type="User"
          :fragments="['Okay, tell me more…']"
        />
      </IsoChatGroup>
    </IsoChatOverlay>
  </div>
</template>

<script lang="ts" setup>
import useCamera from "./useCamera";
import useDisposer from "./useDisposer";
import usePan from "./usePan";
import useThreeElement from "./useThreeElement";
import animate from "./animate";
import delay from "./delay";
import { easeOutCubic } from "@shared/lib/animation/easing";
import { Group, Object3D, Vector3 } from "three";
import { ref, toRefs } from "vue";
import IsoChat from "./IsoChat.vue";
import IsoChatGroup from "./IsoChatGroup.vue";
import IsoChatOverlay from "./IsoChatOverlay.vue";
import IsoPromptBlock from "./IsoPromptBlock";
import IsoTaxGraph from "./IsoTaxGraph";
import Colors from "@cosine/lib/Colors";

const props = defineProps<{
  parent: Object3D,
}>();

defineExpose({
  animateIn,
});

const {
  canvas,
} = useThreeElement();

const {
  tileWidth,
  animateCameraZoom,
} = useCamera();

const {
  panPosition,
  animatePan,
} = usePan();

const {
  parent,
} = toRefs(props);

const overlayRef = ref<InstanceType<typeof IsoChatOverlay>>();
const userChatRef = ref<InstanceType<typeof IsoChat>>();
const systemChatRef = ref<InstanceType<typeof IsoChat>>();
const taxLossHarvestingChatRef = ref<InstanceType<typeof IsoChat>>();
const payrollOptimizationChatRef = ref<InstanceType<typeof IsoChat>>();
const selfChatRef = ref<InstanceType<typeof IsoChat>>();
const confirmChatRef = ref<InstanceType<typeof IsoChat>>();
const overlayVector = ref(new Vector3(0, 0, 0));

const {
  autoDispose,
} = useDisposer();
const group = new Group();
parent.value.add(group);

const isAnimatingBlock = ref(false);
const isAnimatingGraph = ref(false);
let taxWithholdBlock: IsoPromptBlock | void;

async function animateIn () {
  await animateBlockChat();
  await animateBarGraph();
}

async function animateBlockChat () {
  overlayVector.value = new Vector3(0, 0, 0);

  isAnimatingBlock.value = true;

  taxWithholdBlock = autoDispose(new IsoPromptBlock(group));
  group.position.sub(panPosition.value);
  canvas.requestRender();

  overlayRef.value?.animateIn();
  await delay(700);
  await userChatRef.value?.animateIn();
  await taxWithholdBlock.animateThinking(canvas.requestRender);

  userChatRef.value?.animateOut();
  await delay(650);
  await overlayRef.value?.animateOut();
  await taxWithholdBlock.animateDown(canvas.requestRender);

  isAnimatingBlock.value = false;
}

async function animateBarGraph () {
  isAnimatingGraph.value = true;

  if (taxWithholdBlock) { taxWithholdBlock.meshGroup.visible = false; }

  const taxGraph = autoDispose(new IsoTaxGraph(group, {
    color: Colors.purple300,
  }));

  const minTileCount = Math.round((canvas.width / tileWidth.value) - 1);
  const graphTileWidth = 4.5;
  const maxZoom = 1;
  const zoom = Math.min(maxZoom, minTileCount / graphTileWidth);

  const horOffset = graphTileWidth / 2 * (1 - zoom);
  const vertOffset = 0;

  const originalPanPosition = panPosition.value.clone();

  await Promise.all([
    taxGraph.animateIn(canvas.requestRender),
    animateCameraZoom({
      zoom,
      duration: 1000,
    }),
    animatePan({
      position: originalPanPosition.clone().add(new Vector3(-horOffset, vertOffset, horOffset)),
      duration: 1000,
    }),
  ]);

  const block = autoDispose(new IsoPromptBlock(group));
  block.meshGroup.position.z = 2;
  block.meshGroup.position.y = -1.1;

  await Promise.all([
    animate((t) => {
      block.meshGroup.position.y = -1.1 + (0 + 1.1) * t;
    }, {
      delay: 500,
      duration: 500,
      easing: easeOutCubic,
    }),
    taxGraph.animateTaxIn(canvas.requestRender),
    animateCameraZoom({
      zoom: 1,
      duration: 1500,
    }),
    animatePan({
      position: originalPanPosition.clone().add(new Vector3(-1, 0, -1)),
      duration: 1500,
    }),
  ]);

  overlayVector.value = new Vector3(-1, 0, 1);
  overlayRef.value?.animateIn();
  await delay(700);

  await block.animateAnswering(({
    index,
  }) => {
    systemChatRef.value?.animateIn({
      index,
    });
  }, canvas.requestRender);

  await Promise.all([
    animate((t) => {
      const style = systemChatRef.value?.$el.style;
      if (style) { style.opacity = String(1 - t); }
    }, {
      delay: 750,
      duration: 1000,
    }),
    block.animateThinking(canvas.requestRender),
  ]);

  await Promise.all([
    taxGraph.animateTaxChange({
      startPercent: 0.5,
      endPercent: 0.6,
    }, canvas.requestRender),
    await block.animateAnswering(({
      index,
    }) => {
      taxLossHarvestingChatRef.value?.animateIn({
        index,
      });
    }, canvas.requestRender, {
      count: 2,
    }),
  ]);

  await delay(500);

  await Promise.all([
    taxGraph.animateTaxChange({
      startPercent: 0.6,
      endPercent: 0.7,
    }, canvas.requestRender),
    await block.animateAnswering(({
      index,
    }) => {
      payrollOptimizationChatRef.value?.animateIn({
        index,
      });
    }, canvas.requestRender, {
      count: 2,
    }),
  ]);

  await delay(500);

  await Promise.all([
    taxGraph.animateTaxChange({
      startPercent: 0.7,
      endPercent: 0.8,
    }, canvas.requestRender),
    await block.animateAnswering(({
      index,
    }) => {
      selfChatRef.value?.animateIn({
        index,
      });
    }, canvas.requestRender, {
      count: 2,
    }),
  ]);

  await delay(500);

  await Promise.all([confirmChatRef.value?.animateIn(), block.animateResponse(canvas.requestRender)]);

  await delay(500);

  Promise.all([
    systemChatRef,
    taxLossHarvestingChatRef,
    payrollOptimizationChatRef,
    selfChatRef,
    confirmChatRef,
  ].map((chatRef) => chatRef.value?.animateOut()));

  await delay(650);

  await overlayRef.value?.animateOut();
  await Promise.all([
    block.animateDown(canvas.requestRender).then(() => block.animateOut(canvas.requestRender)),
    taxGraph.animateOut(canvas.requestRender),
    animatePan({
      position: panPosition.value.clone().add(new Vector3(-7, 0, 1)),
      duration: 1500,
    }),
  ]);
}
</script>
